<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { deletePostAction, getPostsAction } from '@/api/functions/settings/blogs-action'
import type { ApiPaginationResponse } from '@/api/typings/response.typing'
import type { PostItem } from '@/api/typings/settings.typing'
import { useMutationApi, useQueryApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { settingsTabs } from '@/constants/setting-tabs.ts'
import { getErrorMessage } from '@/utils/errorUtils'
import SettingsOverview from '../partials/SettingsOverview.vue'
import BlogTable from './partials/BlogTable.vue'

const router = useRouter()
const toast = useToast()
const showDeleteModal = ref(false)
const currentPage = ref(1)
const selectedBlogIds = ref<string[]>([])

// --- Query Posts ---
const { data: postsData, refetch } = useQueryApi<ApiPaginationResponse<PostItem[]>>({
  queryKey: computed(() => ['posts', currentPage.value]),
  queryFn: () => getPostsAction(currentPage.value),
  staleTime: 1000 * 60 * 3,
})

// --- Computed rows ---
const posts = computed(() => postsData.value?.data || [])
const pagination = computed(() => postsData.value?.pagination)
const totalItems = computed(() => pagination.value?.total ?? 0)
const itemsPerPage = computed(() => pagination.value?.perPage ?? 5)

const goToPage = (page: number) => {
  currentPage.value = page
  refetch()
}

const goToCreateBlog = () => {
  router.push({ path: `/settings/blog/add` })
}

const deleteMutation = useMutationApi({
  mutationFn: (ids: string[]) => deletePostAction(ids),
  onSuccess: (response) => {
    toast.success(response?.meta?.message || `${selectedBlogIds.value.length} بلاگ حذف شد`)
    showDeleteModal.value = false
    selectedBlogIds.value = []
    refetch()
  },
  onError: (error: unknown) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

const handleDeleteBlog = () => {
  if (selectedBlogIds.value.length === 0) {
    toast.error('لطفاً حداقل یک بلاگ را انتخاب کنید')
    return
  }
  deleteMutation.mutate(selectedBlogIds.value)
}

const handleSelectionChange = (selectedIds: string[]) => {
  selectedBlogIds.value = selectedIds
}
</script>

<template>
    <div class="w-full mb-12">
        <ConfirmModal v-model="showDeleteModal" title="حذف کردن مطالب وبلاگ"
            message="آیا از حذف کردن این مطالب اطمینان دارید؟" confirm-text="بله حذف شود" cancel-text="بازگشت"
            @confirm="handleDeleteBlog" @cancel="showDeleteModal = false" />

        <SettingsOverview :tabs="settingsTabs">
            <template #actions>
                <div class="flex lg:flex-col-reverse items-center gap-2 pb-1">
                    <Button :isAdmin="true" @click="goToCreateBlog"
                        class="px-3! rounded-lg! h-8! py-0 text-[13px] font-medium">
                        ایجاد مطلب جدید
                    </Button>
                    <Button :isAdmin="true" @click="showDeleteModal = true" :disabled="selectedBlogIds.length === 0"
                        variant="outline" class="px-3! rounded-lg! h-8! border-2 py-0 text-[13px] font-medium">
                        حذف کردن
                    </Button>
                </div>
            </template>
        </SettingsOverview>

        <div class="flex flex-col gap-6 mt-20 lg:mt-8">
            <BlogTable :blogs="posts" @refreshBlogs="refetch" @selectionChange="handleSelectionChange" />
            <Pagination v-if="totalItems > 0" v-model:currentPage="currentPage" :itemsPerPage="itemsPerPage"
                :totalItems="totalItems" @update:currentPage="goToPage" :isAdmin="true" />
        </div>
    </div>
</template>
